import { z } from "zod";
import { ufSchema } from "./calculation.js";
/**
 * CONFAZ ST agreement signatory matrix (ADR 0013 notes, "CONFAZ origin
 * scope"). A destination UF's ST annex covers its internal operations;
 * interstate retention by the sender exists only where both the origin and
 * the destination are bound by the convênio/protocolo of the segment on the
 * operation date. Web research reads each agreement's official CONFAZ page
 * (signatories in the preamble, adhesions, denúncias, exclusions and
 * revocation in the header notes, the states named by the consolidated
 * clause, the CESTs it covers or excepts); the API turns those observations
 * into membership intervals. An agreement is UNVERIFIED until a reviewer
 * confirms it against the preserved document: only VERIFIED agreements reach
 * CERTIFIED_ONLY resolution (SHADOW). Nothing here publishes a rule.
 */
const cestSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
const segmentSchema = z.string().regex(/^\d{2}$/);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const dateSchema = z.string().date();
const locatorSchema = z
    .object({
    textStart: z.number().int().nonnegative(),
    textEnd: z.number().int().nonnegative(),
})
    .strict();
export const stAgreementKindSchema = z.enum(["PROTOCOLO", "CONVENIO"]);
/** `PROTOCOLO-41-2008`, `CONVENIO-199-2017`. */
export const stAgreementKeySchema = z.string().regex(/^(PROTOCOLO|CONVENIO)-[1-9]\d{0,3}-\d{4}$/);
export function stAgreementKey(kind, number, year) {
    return `${kind}-${number}-${year}`;
}
/** "Prot. ICMS 41/08", "Conv. ICMS 199/17". */
export function stAgreementLabel(key) {
    const match = /^(PROTOCOLO|CONVENIO)-(\d+)-(\d{4})$/.exec(key);
    if (!match)
        return key;
    const [, kind, number, year] = match;
    return `${kind === "PROTOCOLO" ? "Prot." : "Conv."} ICMS ${number}/${String(year).slice(2)}`;
}
/** Official CONFAZ page of an agreement (the reviewed `confaz-legislacao` source). */
export function stAgreementOfficialUrl(kind, number, year) {
    const yy = String(year).slice(2);
    const nnn = String(number).padStart(3, "0");
    // CONFAZ stores its 2017 protocol collection under `2017-1` (the official
    // SEFAZ-MG 2017 index links pt054_17 there). The unsuffixed path returns 404.
    const folder = kind === "PROTOCOLO" && year === 2017 ? "2017-1" : String(year);
    return kind === "PROTOCOLO"
        ? `https://www.confaz.fazenda.gov.br/legislacao/protocolos/${folder}/pt${nnn}_${yy}`
        : `https://www.confaz.fazenda.gov.br/legislacao/convenios/${year}/CV${nnn}_${yy}`;
}
/** A CONFAZ agreement URL → its key; null for any other page. */
export function stAgreementKeyFromUrl(url) {
    const match = /^https:\/\/(?:www\.)?confaz\.fazenda\.gov\.br\/legislacao\/(protocolos|convenios)\/(\d{4})(-1)?\/(?:pt|cv)(\d{1,4})_(\d{2})\/?(?:[?#].*)?$/i.exec(url);
    if (!match)
        return null;
    const [, folder, year, collectionSuffix, number, yy] = match;
    if (collectionSuffix && (String(folder).toLowerCase() !== "protocolos" || year !== "2017"))
        return null;
    if (String(year).slice(2) !== yy)
        return null;
    return stAgreementKey(String(folder).toLowerCase() === "protocolos" ? "PROTOCOLO" : "CONVENIO", Number(number), Number(year));
}
function definition(kind, number, year, segments, keywords) {
    return {
        key: stAgreementKey(kind, number, year),
        kind,
        number,
        year,
        segments,
        keywords,
        officialUrl: stAgreementOfficialUrl(kind, number, year),
    };
}
/**
 * Reviewed seed: the main ST agreements per Conv. 142/18 segment. It names
 * which official pages the CONFAZ sweep reads; it states no signatory, date
 * or validity (those come from the documents and are reviewed). Revoked
 * agreements stay: they answer operations dated inside their window. Edit
 * only through a reviewed pull request.
 */
export const ST_AGREEMENT_CATALOG = [
    definition("PROTOCOLO", 41, 2008, ["01"], ["autope"]),
    definition("PROTOCOLO", 97, 2010, ["01"], ["autope"]),
    definition("PROTOCOLO", 11, 1991, ["03"], ["cerveja", "refrigerante"]),
    definition("CONVENIO", 111, 2017, ["04"], ["cigarro"]),
    definition("PROTOCOLO", 11, 1985, ["05"], ["cimento"]),
    definition("CONVENIO", 110, 2007, ["06"], ["combust"]),
    definition("PROTOCOLO", 17, 1985, ["09"], ["lâmpada", "lampada"]),
    definition("CONVENIO", 234, 2017, ["13"], ["medicamento"]),
    definition("CONVENIO", 85, 1993, ["16"], ["pneu"]),
    // Segment 17: the food protocols in force in 2026 (SEFAZ-SP list of 22.09.2026 and the CONFAZ
    // pages; ementa "produtos alimentícios"). 45/13 RJ-SP and 108/13 PR-SP keep their own annex.
    definition("PROTOCOLO", 28, 2009, ["17"], ["aliment"]),
    definition("PROTOCOLO", 188, 2009, ["17"], ["aliment"]),
    definition("PROTOCOLO", 114, 2011, ["17"], ["aliment"]),
    definition("PROTOCOLO", 119, 2012, ["17"], ["aliment"]),
    definition("PROTOCOLO", 217, 2012, ["17"], ["aliment"]),
    definition("PROTOCOLO", 30, 2013, ["17"], ["aliment"]),
    definition("PROTOCOLO", 45, 2013, ["17"], ["aliment"]),
    definition("PROTOCOLO", 108, 2013, ["17"], ["aliment"]),
    definition("PROTOCOLO", 175, 2013, ["17"], ["aliment"]),
    definition("PROTOCOLO", 14, 2016, ["17"], ["aliment"]),
    // Segment 20: cosmetics. The SP protocols ended on 31.03.2026 (Portaria SRE 94/2025 took the
    // segment out of SP's ST); they stay for operations dated inside their window.
    definition("PROTOCOLO", 36, 2009, ["20"], ["cosmét", "perfum"]),
    definition("PROTOCOLO", 98, 2009, ["20"], ["cosmét", "perfum"]),
    definition("PROTOCOLO", 164, 2010, ["20"], ["cosmét", "perfum"]),
    definition("PROTOCOLO", 215, 2012, ["20"], ["cosmét", "perfum"]),
    definition("PROTOCOLO", 54, 2017, ["20"], ["cosmét", "perfum"]),
    // Segment 21: electronics and household appliances.
    definition("PROTOCOLO", 15, 2007, ["21"], ["eletr"]),
    definition("PROTOCOLO", 8, 2008, ["21"], ["eletr"]),
    definition("PROTOCOLO", 31, 2009, ["21"], ["eletr"]),
    definition("PROTOCOLO", 192, 2009, ["21"], ["eletr"]),
    definition("PROTOCOLO", 57, 2011, ["21"], ["eletr"]),
    definition("PROTOCOLO", 136, 2013, ["21"], ["eletr"]),
    definition("PROTOCOLO", 171, 2013, ["21"], ["eletr"]),
    definition("PROTOCOLO", 20, 2005, ["23"], ["sorvete"]),
    definition("CONVENIO", 74, 1994, ["24"], ["tinta"]),
    definition("CONVENIO", 199, 2017, ["25"], ["veículo", "veiculo"]),
    definition("CONVENIO", 200, 2017, ["26"], ["duas e três rodas", "duas e tres rodas"]),
    definition("CONVENIO", 45, 1999, ["28"], ["porta a porta"]),
];
/** Conv. 142/18 names of the segments the seed covers (as the CEST catalog prints them). */
export const ST_AGREEMENT_SEGMENT_NAMES = {
    "01": "Autopeças",
    "03": "Cervejas, chopes, refrigerantes, águas e outras bebidas",
    "04": "Cigarros e outros produtos derivados do fumo",
    "05": "Cimentos",
    "06": "Combustíveis e lubrificantes",
    "09": "Lâmpadas, reatores e starter",
    "13": "Medicamentos de uso humano e outros produtos farmacêuticos para uso humano ou veterinário",
    "16": "Pneumáticos, câmaras de ar e protetores de borracha",
    "17": "Produtos alimentícios",
    "20": "Produtos de perfumaria e de higiene pessoal e cosméticos",
    "21": "Produtos eletrônicos, eletroeletrônicos e eletrodomésticos",
    "23": "Sorvetes e preparados para fabricação de sorvetes em máquinas",
    "24": "Tintas e vernizes",
    "25": "Veículos automotores",
    "26": "Veículos de duas e três rodas motorizados",
    "28": "Venda de mercadorias pelo sistema porta a porta",
};
export function stAgreementDefinition(key) {
    return ST_AGREEMENT_CATALOG.find((entry) => entry.key === key);
}
/* -- Worker observation (EXTRACT_DOCUMENT_SEGMENT, extractMode CONFAZ_AGREEMENT) -- */
/** Quote of the passage an observation came from, with its code-point locator. */
const quotedFields = {
    quote: z.string().min(1).max(2_000),
    locator: locatorSchema.nullable().default(null),
};
/**
 * One agreement as the worker read it from its official page. Dates are ISO
 * (the worker reads "01.05.08", "1º de outubro de 2026", "01/10/2026").
 */
export const stAgreementObservationSchema = z
    .object({
    kind: stAgreementKindSchema,
    number: z.number().int().min(1).max(9_999),
    year: z.number().int().min(1975).max(2100),
    /** Ementa ("Dispõe sobre a substituição tributária nas operações … com autopeças"). */
    title: z.string().max(1_000).nullable().default(null),
    /** "Publicado no DOU de 14.04.08". */
    publishedOn: dateSchema.nullable().default(null),
    /** Stated start of effects of the original text; null when not stated. */
    effectiveFrom: dateSchema.nullable().default(null),
    /** First day the agreement is no longer in force ("Revogado … a partir de"). */
    revokedFrom: dateSchema.nullable().default(null),
    revokedBy: z.string().max(80).nullable().default(null),
    /**
     * Convênio of the CONFAZ plenary that names no states: every UF and the
     * DF are bound (minus later exits). Always a check failure (review it).
     */
    plenary: z.boolean().default(false),
    /** States named by the original preamble ("Os Estados do Amapá, …, e o Distrito Federal"). */
    signatories: z
        .array(z.object({ uf: ufSchema, ...quotedFields }).strict())
        .max(27)
        .default([]),
    /** Adhesions, denúncias and exclusions stated by the header notes or clauses. */
    changes: z
        .array(z
        .object({
        uf: ufSchema,
        change: z.enum(["ADESAO", "DENUNCIA", "EXCLUSAO"]),
        /** First day of the change: joins on it, or is no longer bound from it. */
        from: dateSchema,
        act: z.string().max(80).nullable().default(null),
        ...quotedFields,
    })
        .strict())
        .max(200)
        .default([]),
    /** States the consolidated clause names ("… destinadas aos Estados do Acre, …"). */
    currentClauseUfs: z.array(ufSchema).max(27).nullable().default(null),
    currentClauseQuote: z.string().max(4_000).nullable().default(null),
    /** CESTs the agreement lists (empty = the whole segment annex). */
    cests: z.array(cestSchema).max(1_000).default([]),
    /** CESTs it excepts ("com exceção aos … CEST 01.019.00, …"). */
    excludedCests: z.array(cestSchema).max(200).default([]),
    checks: z
        .object({
        passed: z.boolean(),
        failures: z.array(z.string().min(1).max(120)).max(40),
    })
        .strict(),
})
    .strict();
/* -- Membership intervals ---------------------------------------------------------- */
export const stAgreementMembershipSchema = z
    .object({
    uf: ufSchema,
    /** First day bound. */
    from: dateSchema,
    /** Last day bound; null = still bound. */
    to: dateSchema.nullable(),
    /**
     * ORIGINAL: preamble signatory; ADHESION: stated adhesion; PLENARY:
     * convênio naming no states; OBSERVED: named by the consolidated clause
     * with no stated adhesion (bound from the capture date only).
     */
    joinedBy: z.enum(["ORIGINAL", "ADHESION", "PLENARY", "OBSERVED"]),
    leftBy: z.enum(["DENUNCIA", "EXCLUSAO", "REVOGACAO"]).nullable(),
    act: z.string().max(80).nullable(),
    quote: z.string().max(2_000).nullable(),
    locator: locatorSchema.nullable(),
})
    .strict();
const ALL_UF_CODES = ufSchema.options;
const addDays = (date, days) => {
    const value = new Date(`${date}T00:00:00.000Z`);
    value.setUTCDate(value.getUTCDate() + days);
    return value.toISOString().slice(0, 10);
};
/**
 * Membership intervals from an observation (pure, deterministic):
 * - original signatories (or every UF for a plenary convênio) are bound from
 *   the stated effects start, else the DOU publication, else the capture date
 *   (then `EFFECTIVE_DATE_INFERRED`);
 * - an adhesion opens an interval on its date; a denúncia or exclusion closes
 *   the open one the day before its date; changes apply in date order;
 * - a revocation closes every open interval the day before;
 * - a state the consolidated clause names that no interval binds at the end
 *   is bound from the capture date only (`OBSERVED`), and any difference
 *   between the clause and the final membership is `CURRENT_CLAUSE_MISMATCH`.
 */
export function agreementMemberships(observation, observedOn) {
    const failures = [];
    const validFrom = observation.effectiveFrom ?? observation.publishedOn ?? observedOn;
    if (!observation.effectiveFrom)
        failures.push("EFFECTIVE_DATE_INFERRED");
    const open = new Map();
    const closed = [];
    const preamble = observation.plenary
        ? ALL_UF_CODES.map((uf) => ({ uf, quote: null, locator: null }))
        : observation.signatories.map((entry) => ({
            uf: entry.uf,
            quote: entry.quote,
            locator: entry.locator ?? null,
        }));
    if (observation.plenary)
        failures.push("PLENARY_SIGNATORIES");
    if (preamble.length === 0)
        failures.push("NO_SIGNATORIES");
    for (const entry of preamble) {
        if (open.has(entry.uf))
            continue;
        open.set(entry.uf, {
            uf: entry.uf,
            from: validFrom,
            to: null,
            joinedBy: observation.plenary ? "PLENARY" : "ORIGINAL",
            leftBy: null,
            act: null,
            quote: entry.quote,
            locator: entry.locator,
        });
    }
    const changes = [...observation.changes].sort((left, right) => left.from.localeCompare(right.from) ||
        // Same day: an exit before a (re)adhesion.
        (left.change === "ADESAO" ? 1 : 0) - (right.change === "ADESAO" ? 1 : 0));
    for (const change of changes) {
        const current = open.get(change.uf);
        if (change.change === "ADESAO") {
            if (current) {
                if (change.from < current.from)
                    current.from = change.from;
                continue;
            }
            open.set(change.uf, {
                uf: change.uf,
                from: change.from,
                to: null,
                joinedBy: "ADHESION",
                leftBy: null,
                act: change.act ?? null,
                quote: change.quote,
                locator: change.locator ?? null,
            });
            continue;
        }
        if (!current) {
            failures.push(`EXIT_WITHOUT_MEMBERSHIP:${change.uf}`);
            continue;
        }
        const to = addDays(change.from, -1);
        if (to < current.from) {
            failures.push(`EXIT_BEFORE_JOIN:${change.uf}`);
            open.delete(change.uf);
            continue;
        }
        closed.push({
            ...current,
            to,
            leftBy: change.change,
            act: change.act ?? current.act,
            quote: change.quote,
            locator: change.locator ?? null,
        });
        open.delete(change.uf);
    }
    if (observation.revokedFrom) {
        const to = addDays(observation.revokedFrom, -1);
        for (const [uf, current] of open) {
            closed.push({ ...current, to: to < current.from ? current.from : to, leftBy: "REVOGACAO" });
            open.delete(uf);
        }
    }
    if (observation.currentClauseUfs && !observation.revokedFrom) {
        const clause = new Set(observation.currentClauseUfs);
        for (const uf of clause) {
            if (open.has(uf))
                continue;
            open.set(uf, {
                uf: uf,
                from: observedOn,
                to: null,
                joinedBy: "OBSERVED",
                leftBy: null,
                act: null,
                quote: observation.currentClauseQuote?.slice(0, 2_000) ?? null,
                locator: null,
            });
        }
        // The destination list excludes a state that still has an open interval.
        const extra = [...open.keys()].filter((uf) => !clause.has(uf));
        const observed = [...open.values()].filter((entry) => entry.joinedBy === "OBSERVED");
        if (extra.length > 0 || observed.length > 0) {
            failures.push(`CURRENT_CLAUSE_MISMATCH:${[...extra.map((uf) => `+${uf}`), ...observed.map((entry) => `?${entry.uf}`)].sort().join(",")}`.slice(0, 120));
        }
    }
    const memberships = [...closed, ...open.values()].sort((left, right) => left.uf.localeCompare(right.uf) || left.from.localeCompare(right.from));
    return { memberships, validFrom, failures: [...new Set(failures)].slice(0, 40) };
}
/* -- Read model ------------------------------------------------------------------------ */
export const stAgreementStatusSchema = z.enum(["UNVERIFIED", "VERIFIED", "REJECTED", "SUPERSEDED"]);
export const stAgreementSchema = z
    .object({
    id: z.string().uuid(),
    key: stAgreementKeySchema,
    kind: stAgreementKindSchema,
    number: z.number().int().positive(),
    year: z.number().int(),
    title: z.string().max(1_000).nullable(),
    /** In the reviewed seed catalog (false: discovered by a CONFAZ locate). */
    seeded: z.boolean(),
    segments: z.array(segmentSchema).max(40),
    cests: z.array(cestSchema).max(1_000),
    excludedCests: z.array(cestSchema).max(200),
    status: stAgreementStatusSchema,
    /** VERIFIED → VERIFIED; UNVERIFIED → UNVERIFIED; otherwise NONE. */
    level: z.enum(["VERIFIED", "UNVERIFIED", "NONE"]),
    publishedOn: dateSchema.nullable(),
    validFrom: dateSchema,
    /** Last day in force (revocation − 1); null = in force. */
    validTo: dateSchema.nullable(),
    revokedBy: z.string().max(80).nullable(),
    memberships: z.array(stAgreementMembershipSchema).max(120),
    currentClauseUfs: z.array(ufSchema).max(27).nullable(),
    checks: z
        .object({ passed: z.boolean(), failures: z.array(z.string().max(120)).max(60) })
        .strict(),
    officialUrl: z.string().url().max(2_000),
    evidenceId: z.string().uuid(),
    contentSha256: sha256Schema,
    extractedAt: z.string().datetime(),
    verifiedBy: z.string().uuid().nullable(),
    verifiedAt: z.string().datetime().nullable(),
    supersededById: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /** ETag for the verification If-Match. */
    revision: z.number().int().positive(),
})
    .strict();
export const stAgreementEventSchema = z
    .object({
    id: z.string().uuid(),
    agreementId: z.string().uuid(),
    action: z.enum(["DERIVED", "RECHECKED", "CONFIRMED", "REJECTED", "SUPERSEDED"]),
    status: stAgreementStatusSchema,
    actorId: z.string().uuid().nullable(),
    machineSubject: z.string().max(200).nullable(),
    relatedAgreementId: z.string().uuid().nullable(),
    note: z.string().max(1_000).nullable(),
    at: z.string().datetime(),
})
    .strict();
/** One seed entry and what the matrix holds for it. */
export const stAgreementCatalogEntrySchema = z
    .object({
    key: stAgreementKeySchema,
    segments: z.array(segmentSchema).max(40),
    officialUrl: z.string().url(),
    /** The version resolution uses: VERIFIED, else the latest UNVERIFIED; null = not ingested. */
    agreementId: z.string().uuid().nullable(),
    status: stAgreementStatusSchema.nullable(),
})
    .strict();
export const stAgreementPageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    items: z.array(stAgreementSchema).max(200),
    catalog: z.array(stAgreementCatalogEntrySchema).max(200),
    counts: z
        .object({
        unverified: z.number().int().nonnegative(),
        verified: z.number().int().nonnegative(),
        rejected: z.number().int().nonnegative(),
        superseded: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
export const stAgreementDetailSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    agreement: stAgreementSchema,
    /** Oldest first. */
    events: z.array(stAgreementEventSchema).max(500),
    /** Other versions of the same agreement, newest first. */
    versions: z
        .array(z
        .object({
        id: z.string().uuid(),
        status: stAgreementStatusSchema,
        contentSha256: sha256Schema,
        extractedAt: z.string().datetime(),
    })
        .strict())
        .max(50),
    document: z
        .object({
        evidenceId: z.string().uuid(),
        title: z.string().max(1_000).nullable(),
        url: z.string().max(2_000),
        fetchedAt: z.string().datetime().nullable(),
    })
        .strict()
        .nullable(),
})
    .strict();
export const ST_AGREEMENT_REJECT_REASON_MIN = 10;
/** REVIEW:TAX_RULE:ALL with If-Match: the agreement revision. */
export const stAgreementVerificationSchema = z.discriminatedUnion("action", [
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        action: z.literal("CONFIRM"),
        note: z.string().trim().max(1_000).optional(),
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        action: z.literal("REJECT"),
        reason: z.string().trim().min(ST_AGREEMENT_REJECT_REASON_MIN).max(1_000),
    })
        .strict(),
]);
/**
 * `POST /v1/st-agreements:retire-misidentified` (REVIEW:TAX_RULE:ALL): rejects
 * UNVERIFIED observations stored under a key their page does not name. Two
 * signatures, both from the parser bug of sweep b431d05d (a listing line read
 * as the page header, so Conv. ICMS 45/99 was stored as 1/99):
 * - `KEY_MISMATCH_SEED`: a non-seed row whose unit expected a seed agreement;
 * - `URL_KEY_ONLY`: a non-seed row with no header, named only by its URL.
 * VERIFIED, seeded and already closed rows are never touched. `dryRun` lists
 * the candidates without writing; a run rejects each one with an audited
 * event carrying `reason`.
 */
export const stAgreementRetireMisidentifiedSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    dryRun: z.boolean(),
    reason: z.string().trim().min(ST_AGREEMENT_REJECT_REASON_MIN).max(1_000),
})
    .strict();
export const stAgreementMisidentifiedReasonSchema = z.enum(["KEY_MISMATCH_SEED", "URL_KEY_ONLY"]);
export const stAgreementRetireMisidentifiedResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    dryRun: z.boolean(),
    candidates: z
        .array(z
        .object({
        agreementId: z.string().uuid(),
        key: stAgreementKeySchema,
        expectedKey: stAgreementKeySchema.nullable(),
        reason: stAgreementMisidentifiedReasonSchema,
    })
        .strict())
        .max(500),
    retired: z.number().int().nonnegative(),
})
    .strict();
const usable = (agreement) => agreement.status === "VERIFIED" || agreement.status === "UNVERIFIED";
/** The agreement governs this segment (or CEST): listed, not excepted. */
export function agreementCovers(agreement, target) {
    const segment = target.segment ?? target.cest?.slice(0, 2) ?? null;
    if (!segment)
        return false;
    if (target.cest && agreement.excludedCests.includes(target.cest))
        return false;
    if (agreement.cests.length > 0) {
        if (target.cest)
            return agreement.cests.includes(target.cest);
        return agreement.cests.some((cest) => cest.startsWith(`${segment}.`));
    }
    return agreement.segments.includes(segment);
}
export function agreementInForce(agreement, date) {
    return agreement.validFrom <= date && (agreement.validTo === null || agreement.validTo >= date);
}
export function agreementMemberOn(agreement, uf, date) {
    return agreement.memberships.find((entry) => entry.uf === uf && entry.from <= date && (entry.to === null || entry.to >= date));
}
/** Members bound on a date (an agreement not in force binds nobody). */
export function agreementMembersOn(agreement, date) {
    if (!agreementInForce(agreement, date))
        return [];
    return [
        ...new Set(agreement.memberships
            .filter((entry) => entry.from <= date && (entry.to === null || entry.to >= date))
            .map((entry) => entry.uf)),
    ].sort();
}
/** The latest usable version per key: VERIFIED wins over UNVERIFIED. */
export function effectiveAgreements(agreements) {
    const byKey = new Map();
    for (const agreement of agreements) {
        if (!usable(agreement))
            continue;
        const current = byKey.get(agreement.key);
        if (!current || (current.status !== "VERIFIED" && agreement.status === "VERIFIED")) {
            byKey.set(agreement.key, agreement);
        }
    }
    return [...byKey.values()].sort((left, right) => left.key.localeCompare(right.key));
}
/** Interstate route status on a date (internal routes are never decided here). */
export function agreementRouteStatus(agreements, route) {
    const covering = effectiveAgreements(agreements).filter((agreement) => agreementCovers(agreement, route));
    const levelOf = (list) => list.length === 0
        ? null
        : list.some((agreement) => agreement.status === "VERIFIED")
            ? "VERIFIED"
            : "UNVERIFIED";
    const both = covering.filter((agreement) => {
        const members = agreementMembersOn(agreement, route.date);
        return members.includes(route.destinationUf) && members.includes(route.originUf);
    });
    if (route.originUf !== route.destinationUf && both.length > 0) {
        return { status: "SIGNATORY", level: levelOf(both), agreementKeys: both.map((a) => a.key) };
    }
    const concerned = covering.filter((agreement) => agreementMembersOn(agreement, route.date).includes(route.destinationUf) ||
        agreement.memberships.some((entry) => entry.uf === route.destinationUf &&
            entry.to !== null &&
            entry.to < route.date &&
            entry.leftBy !== null));
    if (route.originUf !== route.destinationUf && concerned.length > 0) {
        // Only as strong as the weakest agreement that concerns the destination.
        const level = concerned.every((agreement) => agreement.status === "VERIFIED")
            ? "VERIFIED"
            : "UNVERIFIED";
        return { status: "NOT_SIGNATORY", level, agreementKeys: concerned.map((a) => a.key) };
    }
    return { status: "UNKNOWN", level: null, agreementKeys: [] };
}
/** Origins whose sales into the destination carry ST on a date (internal always included). */
export function derivedOriginScope(agreements, target) {
    const interstate = [];
    const keys = new Set();
    const levels = new Set();
    let known = false;
    for (const originUf of ALL_UF_CODES) {
        if (originUf === target.destinationUf)
            continue;
        const route = agreementRouteStatus(agreements, { ...target, originUf });
        if (route.status === "UNKNOWN")
            continue;
        known = true;
        for (const key of route.agreementKeys)
            keys.add(key);
        if (route.level)
            levels.add(route.level);
        if (route.status === "SIGNATORY")
            interstate.push(originUf);
    }
    return {
        originUfs: [target.destinationUf, ...interstate].sort(),
        interstateOriginUfs: interstate.sort(),
        known,
        level: !known ? null : levels.has("UNVERIFIED") ? "UNVERIFIED" : "VERIFIED",
        agreementKeys: [...keys].sort(),
    };
}
/** Membership changes of covering agreements in `(from, to]` (for "SP sai em 01/10/2026"). */
export function agreementChangesBetween(agreements, target, from, to) {
    const changes = [];
    for (const agreement of effectiveAgreements(agreements)) {
        if (!agreementCovers(agreement, target))
            continue;
        for (const entry of agreement.memberships) {
            if (entry.from > from && entry.from <= to) {
                changes.push({
                    uf: entry.uf,
                    on: entry.from,
                    change: "JOINS",
                    agreementKey: agreement.key,
                });
            }
            if (entry.to !== null) {
                const leaves = addDays(entry.to, 1);
                if (leaves > from && leaves <= to) {
                    changes.push({ uf: entry.uf, on: leaves, change: "LEAVES", agreementKey: agreement.key });
                }
            }
        }
        if (agreement.validTo !== null) {
            const ends = addDays(agreement.validTo, 1);
            if (ends > from && ends <= to) {
                for (const uf of agreementMembersOn(agreement, agreement.validTo)) {
                    if (!changes.some((c) => c.uf === uf && c.on === ends && c.change === "LEAVES")) {
                        changes.push({ uf, on: ends, change: "LEAVES", agreementKey: agreement.key });
                    }
                }
            }
        }
    }
    return changes.sort((left, right) => left.on.localeCompare(right.on) || left.uf.localeCompare(right.uf));
}
//# sourceMappingURL=st-agreements.js.map